/**
 * BookNowButton — the primary conversion CTA on the Smart Report.
 *
 * Contract:
 *   • Every click stays with the parent lab that generated the report.
 *   • Booking mode is fetched from `/api/labs/{lab_id}/booking-config`.
 *     - `whatsapp` → open wa.me with a pre-filled message.
 *     - `efgh_page` → navigate to the public /l/{slug}/book route (fallback when whatsapp number is missing).
 *     - `lab_api`   → v1: falls back to `efgh_page` until the lab wires their API.
 *   • The user chose `Fall back to the EFGH booking page even if the lab prefers WhatsApp` when the report
 *     has no patient mobile, so that logic lives here — see `shouldFallBackToEfgh()`.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/lib/settings";

const shouldFallBackToEfgh = (cfg, patientPhone) => {
  if (!cfg) return true;
  if (cfg.effective_mode !== "whatsapp") return true;
  if (!cfg.whatsapp_number) return true;
  if (!patientPhone || !String(patientPhone).trim()) return true; // per user rule
  return false;
};

const buildWhatsAppMessage = ({ lab, report, tests, patientName, patientPhone, reportUrl }) => {
  const list = tests.map((t) => `• ${t}`).join("\n");
  return [
    `Hello ${lab?.name || "Lab"},`,
    ``,
    `I would like to book the following recommended test(s):`,
    list,
    ``,
    `Patient Name: ${patientName || "(please ask)"}`,
    `Mobile: ${patientPhone || "(please ask)"}`,
    report?.id ? `Report ID: ${report.id.slice(0, 8)}` : ``,
    `Preferred collection: Home Collection`,
    reportUrl ? `Smart Report: ${reportUrl}` : ``,
    ``,
    `Please contact me to confirm the booking.`,
  ]
    .filter(Boolean)
    .join("\n");
};

const sanitizeWhatsAppNumber = (raw) => String(raw || "").replace(/[^\d]/g, "");

export function BookNowButton({
  labId,
  lab,
  report,
  tests,
  patientName,
  patientPhone,
  reportUrl,
  size = "sm",
  fullWidth = false,
  variant = "primary",
  testid,
  children,
}) {
  const nav = useNavigate();
  const { platform } = useSiteSettings();
  const marketplaceOn = platform?.marketplace_enabled !== false;
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async () => {
    // Patient-uploaded reports (no lab attached) route to EFGH's own marketplace booking flow.
    if (!labId) {
      const query = new URLSearchParams();
      if (tests?.length) query.set("tests", tests.join("|"));
      if (report?.id) query.set("reportId", report.id);
      nav(`/book-tests?${query.toString()}`);
      return;
    }
    setLoading(true);
    try {
      const { data: cfg } = await api.get(`/labs/${labId}/booking-config`);
      const fallback = shouldFallBackToEfgh(cfg, patientPhone);
      if (!fallback && cfg.effective_mode === "whatsapp") {
        const num = sanitizeWhatsAppNumber(cfg.whatsapp_number);
        const msg = buildWhatsAppMessage({
          lab: lab || { name: cfg.lab_name },
          report,
          tests,
          patientName,
          patientPhone,
          reportUrl,
        });
        const url = `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      // Fallback / efgh_page mode → deep-link to the public booking page
      const slug = cfg.slug || lab?.slug;
      if (!slug) {
        toast.error("This lab hasn't finished setup yet. Please contact them directly.");
        return;
      }
      const query = new URLSearchParams();
      if (tests?.length) query.set("tests", tests.join("|"));
      if (report?.id) query.set("reportId", report.id);
      if (patientName) query.set("name", patientName);
      if (patientPhone) query.set("phone", patientPhone);
      nav(`/l/${slug}/book?${query.toString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open booking. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [labId, lab, report, tests, patientName, patientPhone, reportUrl, nav]);

  const cls =
    variant === "primary"
      ? "bg-ink hover:bg-ink/90 text-white"
      : "bg-white text-ink border border-ink/20 hover:bg-ink/5";

  // When the platform marketplace is OFF and this report has no owning lab,
  // there is nowhere for the CTA to go — hide the button. Reports attached to
  // a specific lab still work (they route to the lab's WhatsApp / booking page).
  if (!labId && !marketplaceOn) return null;

  return (
    <Button
      data-testid={testid || "book-now-btn"}
      size={size}
      className={`${cls} ${fullWidth ? "w-full" : ""}`}
      onClick={handleClick}
      disabled={loading}
    >
      {loading ? (
        <>
          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Opening…
        </>
      ) : (
        <>
          {children || "Book Now"} <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </>
      )}
    </Button>
  );
}
